import * as Sentry from '@sentry/react'

const dsn = import.meta.env.VITE_SENTRY_DSN
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development'

/**
 * Redact sensitive fields from Sentry events before sending.
 * Filters: JWT tokens, passwords, refresh tokens, auth headers, image pixel data.
 */
function beforeSend(event: Sentry.ErrorEvent, _hint: Sentry.EventHint): Sentry.ErrorEvent | null {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void _hint
  // Drop events that may contain sensitive authentication data
  if (event.request) {
    const headers = event.request.headers as Record<string, string> | undefined
    if (headers) {
      const lowerHeaders: Record<string, string> = {}
      for (const [key, value] of Object.entries(headers)) {
        lowerHeaders[key.toLowerCase()] = value
      }
      if (
        lowerHeaders['authorization'] ||
        lowerHeaders['x-auth-token']
      ) {
        event.request.headers = { ...headers, Authorization: '[REDACTED]' }
      }
    }
    if (event.request.data) {
      const data = event.request.data as Record<string, unknown>
      if (typeof data === 'object' && data !== null) {
        if ('password' in data) data.password = '[REDACTED]'
        if ('refresh_token' in data) data.refresh_token = '[REDACTED]'
        if ('access_token' in data) data.access_token = '[REDACTED]'
        if ('token' in data) data.token = '[REDACTED]'
      }
    }
  }

  // Scrub user context of PII (preserve ip_address per PM decision)
  if (event.user) {
    const ip = event.user.ip_address
    event.user = { ip_address: ip }
  }

  return event
}

export function initSentry(): void {
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn('[Sentry] VITE_SENTRY_DSN not set; Sentry is disabled.')
    return
  }

  Sentry.init({
    dsn,
    environment,
    release: `depth-website@${__APP_VERSION__}`,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [
      /\/api/,
      new RegExp(
        `^${(import.meta.env.VITE_API_BASE_URL || window.location.origin).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
      ),
    ],
    beforeSend,
  })
}
